import { Pause, Play } from 'lucide-react';

interface ActivityFiltersProps {
  hours: number;
  type: string;
  status: string;
  eventCount: number;
  paused: boolean;
  onHoursChange: (hours: number) => void;
  onTypeChange: (type: string) => void;
  onStatusChange: (status: string) => void;
  onTogglePause: () => void;
}

const TIME_OPTIONS = [
  { label: '1h', value: 1 },
  { label: '6h', value: 6 },
  { label: '24h', value: 24 },
  { label: '7d', value: 168 },
] as const;

const TYPE_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Jobs', value: 'job_run' },
  { label: 'Sync', value: 'sync' },
] as const;

const STATUS_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Success', value: 'success' },
  { label: 'Error', value: 'error' },
  { label: 'Running', value: 'running' },
] as const;

function ToggleGroup({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<{ label: string; value: string | number }>;
  value: string | number;
  onChange: (val: never) => void;
}) {
  return (
    <div className="flex rounded-lg overflow-hidden border border-border-subtle">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value as never)}
          className={`px-2 sm:px-3 py-1.5 text-xs font-medium transition-colors ${
            value === opt.value
              ? 'bg-accent-purple text-white'
              : 'bg-bg-tertiary text-text-secondary hover:text-text-primary hover:bg-bg-tertiary/80'
          } ${
            opt !== options[0] ? 'border-l border-border-subtle' : ''
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function ActivityFilters({
  hours,
  type,
  status,
  eventCount,
  paused,
  onHoursChange,
  onTypeChange,
  onStatusChange,
  onTogglePause,
}: ActivityFiltersProps) {
  return (
    <div className="bg-bg-secondary rounded-lg p-3 flex flex-row gap-2 sm:gap-4 items-center flex-wrap">
      <ToggleGroup
        options={TIME_OPTIONS}
        value={hours}
        onChange={onHoursChange}
      />
      <ToggleGroup
        options={TYPE_OPTIONS}
        value={type}
        onChange={onTypeChange}
      />
      <ToggleGroup
        options={STATUS_OPTIONS}
        value={status}
        onChange={onStatusChange}
      />

      <div className="ml-auto flex items-center gap-3">
        <span className="text-xs text-text-muted">
          Showing {eventCount} event{eventCount !== 1 ? 's' : ''}
        </span>
        <button
          onClick={onTogglePause}
          className={`p-1.5 rounded transition-colors ${
            paused
              ? 'text-warning hover:text-warning/80'
              : 'text-text-muted hover:text-text-secondary'
          }`}
          aria-label={paused ? 'Resume polling' : 'Pause polling'}
          title={paused ? 'Resume auto-refresh' : 'Pause auto-refresh'}
        >
          {paused ? (
            <Play className="h-4 w-4" />
          ) : (
            <Pause className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

export default ActivityFilters;
